'use client';

/**
 * Pricing Context Panel Component
 *
 * Displays pricing context including:
 * - Service taxonomy default rates
 * - ML pricing recommendations
 * - Market comparatives (Medicare, Medicaid, Commercial)
 * - Provider's accepted rate (if already accepted)
 * - Rate range guidance
 *
 * Used in both referral creation and pricing negotiation workflows.
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  CheckCircle,
  AlertCircle,
  Loader2,
  Brain,
  BarChart3,
  Building2,
  Heart,
  Briefcase,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient } from '@/services/apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PricingContextPanelProps {
  serviceTypeCode: string;
  providerType?: string;
  providerId?: number | string;
  urgencyLevel?: 'routine' | 'urgent' | 'emergent';
  acceptedRate?: number;
  acceptedAt?: string;
  providerName?: string;
  showComparatives?: boolean;
  showMLRecommendation?: boolean;
  compact?: boolean;
  className?: string;
}

interface PricingContext {
  service_type: {
    code: string;
    name: string;
    category: string;
    default_rate: number;
    billing_unit: string;
    cpt_code?: string;
    hcpcs_code?: string;
  };
  rate_card?: {
    standard_rate: number;
    urgent_rate?: number;
    emergent_rate?: number;
    rate_range?: { low: number; high: number };
    preferred_pricing_model?: string;
    is_default?: boolean;
  };
  market_comparatives?: {
    medicare_rate?: number;
    medicaid_rate?: number;
    commercial_rate?: number;
    market_average?: number;
    market_median?: number;
  };
  ml_recommendation?: {
    predicted_rate: number;
    confidence: number;
    rate_range: { low: number; high: number };
    recommendation_type: string;
    feature_importance?: Record<string, number>;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return 'text-green-600';
  if (confidence >= 0.6) return 'text-yellow-600';
  return 'text-red-600';
};

// Note: This function returns keys for translation
const getConfidenceLabelKey = (confidence: number): { key: string; default: string } => {
  if (confidence >= 0.8) return { key: 'serviceReferrals.pricing.confidenceHigh', default: 'High' };
  if (confidence >= 0.6) return { key: 'serviceReferrals.pricing.confidenceMedium', default: 'Medium' };
  return { key: 'serviceReferrals.pricing.confidenceLow', default: 'Low' };
};

const getRateComparisonIcon = (rate: number, benchmark: number) => {
  const diff = ((rate - benchmark) / benchmark) * 100;
  if (Math.abs(diff) < 5) return <Minus className="h-3 w-3 text-gray-500" />;
  if (diff > 0) return <TrendingUp className="h-3 w-3 text-red-500" />;
  return <TrendingDown className="h-3 w-3 text-green-500" />;
};

// ============================================================================
// COMPONENT
// ============================================================================

export function PricingContextPanel({
  serviceTypeCode,
  providerType = 'cbo',
  providerId,
  urgencyLevel = 'routine',
  acceptedRate,
  acceptedAt,
  providerName,
  showComparatives = true,
  showMLRecommendation = true,
  compact = false,
  className = '',
}: PricingContextPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricingContext, setPricingContext] = useState<PricingContext | null>(null);

  useEffect(() => {
    loadPricingContext();
  }, [serviceTypeCode, providerType, providerId]);

  const loadPricingContext = async () => {
    if (!serviceTypeCode) return;

    setLoading(true);
    setError(null);

    try {
      // Load pricing context from backend
      const response = await apiClient.get<{ success: boolean; data: PricingContext }>(
        `/api/pricing/context/${serviceTypeCode}`,
        {
          params: {
            provider_type: providerType,
            provider_id: providerId,
            urgency: urgencyLevel,
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        setPricingContext(response.data.data);
      } else {
        // Fallback to rate card endpoint
        const rateCardResponse = await apiClient.get(
          `/api/pricing-proposals/rate-cards/${providerType}/${providerId}/${serviceTypeCode}`
        );

        if (rateCardResponse.data?.success && rateCardResponse.data?.rate_card) {
          const rateCard = rateCardResponse.data.rate_card;
          setPricingContext({
            service_type: {
              code: serviceTypeCode,
              name: rateCard.service_name || serviceTypeCode,
              category: 'Service',
              default_rate: rateCard.standard_rate,
              billing_unit: rateCard.billing_unit || 'per_service',
            },
            rate_card: {
              standard_rate: rateCard.standard_rate,
              urgent_rate: rateCard.urgent_rate,
              emergent_rate: rateCard.emergent_rate,
              rate_range: rateCard.rate_range,
              is_default: rateCard.is_default,
            },
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load pricing context:', err);
      setError('Unable to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  // Get the appropriate rate based on urgency
  const getUrgencyRate = (): number | undefined => {
    if (!pricingContext?.rate_card) return undefined;

    switch (urgencyLevel) {
      case 'emergent':
        return pricingContext.rate_card.emergent_rate || pricingContext.rate_card.standard_rate;
      case 'urgent':
        return pricingContext.rate_card.urgent_rate || pricingContext.rate_card.standard_rate;
      default:
        return pricingContext.rate_card.standard_rate;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t('serviceReferrals.pricing.loadingContext', 'Loading pricing context...')}</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
          <p className="text-gray-500">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const currentRate = getUrgencyRate();
  const mlRate = pricingContext?.ml_recommendation?.predicted_rate;
  const mlConfidence = pricingContext?.ml_recommendation?.confidence || 0;

  return (
    <Card className={className}>
      <CardHeader className={compact ? 'pb-2' : ''}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            <CardTitle className={compact ? 'text-base' : ''}>{t('serviceReferrals.pricing.title', 'Pricing Context')}</CardTitle>
          </div>
          {acceptedRate && (
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              {t('serviceReferrals.pricing.accepted', 'Accepted')}: {formatCurrency(acceptedRate)}
            </Badge>
          )}
        </div>
        {!compact && pricingContext?.service_type && (
          <CardDescription>
            {pricingContext.service_type.name} ({pricingContext.service_type.code})
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className={compact ? 'pt-0' : ''}>
        {/* Accepted Rate Display */}
        {acceptedRate && (
          <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">{t('serviceReferrals.pricing.providerAcceptedRate', 'Provider Accepted Rate')}</p>
                {providerName && <p className="text-xs text-green-600">{providerName}</p>}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-700">{formatCurrency(acceptedRate)}</p>
                {acceptedAt && (
                  <p className="text-xs text-green-600">
                    {t('serviceReferrals.pricing.accepted', 'Accepted')} {fmt.short(acceptedAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Service Rate Card */}
        {pricingContext?.rate_card && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('serviceReferrals.pricing.serviceRate', 'Service Rate')}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{formatCurrency(currentRate)}</span>
                {urgencyLevel !== 'routine' && (
                  <Badge variant="outline" className="text-xs">
                    {urgencyLevel === 'urgent' ? '+25%' : '+50%'}
                  </Badge>
                )}
              </div>
            </div>

            {pricingContext.rate_card.rate_range && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t('serviceReferrals.pricing.rateRange', 'Rate Range')}</span>
                <span className="text-gray-700">
                  {formatCurrency(pricingContext.rate_card.rate_range.low)} - {formatCurrency(pricingContext.rate_card.rate_range.high)}
                </span>
              </div>
            )}

            {pricingContext.rate_card.is_default && (
              <p className="text-xs text-gray-400 italic">
                * {t('serviceReferrals.pricing.usingDefaultRates', 'Using service taxonomy default rates')}
              </p>
            )}
          </div>
        )}

        {/* ML Recommendation */}
        {showMLRecommendation && pricingContext?.ml_recommendation && (
          <>
            <Separator className="my-4" />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">{t('serviceReferrals.pricing.mlRecommendation', 'ML Recommendation')}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3 w-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs max-w-xs">
                        {t('serviceReferrals.pricing.mlTooltip', 'Based on historical data, provider quality, and market conditions')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-purple-700">
                    {formatCurrency(mlRate)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('serviceReferrals.pricing.range', 'Range')}: {formatCurrency(pricingContext.ml_recommendation.rate_range.low)} - {formatCurrency(pricingContext.ml_recommendation.rate_range.high)}
                  </p>
                </div>
                <div className="text-right">
                  <Badge variant="outline" className={getConfidenceColor(mlConfidence)}>
                    {t(getConfidenceLabelKey(mlConfidence).key, getConfidenceLabelKey(mlConfidence).default)} {t('serviceReferrals.pricing.confidence', 'Confidence')}
                  </Badge>
                  <p className="text-xs text-gray-400 mt-1">
                    {(mlConfidence * 100).toFixed(0)}%
                  </p>
                </div>
              </div>

              <Progress value={mlConfidence * 100} className="h-1" />
            </div>
          </>
        )}

        {/* Market Comparatives */}
        {showComparatives && pricingContext?.market_comparatives && (
          <>
            <Separator className="my-4" />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">{t('serviceReferrals.pricing.marketComparatives', 'Market Comparatives')}</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {pricingContext.market_comparatives.medicare_rate && (
                  <div className="text-center p-2 bg-blue-50 rounded">
                    <Building2 className="h-4 w-4 mx-auto text-blue-600 mb-1" />
                    <p className="text-xs text-gray-500">{t('serviceReferrals.pricing.medicare', 'Medicare')}</p>
                    <p className="font-semibold text-sm">
                      {formatCurrency(pricingContext.market_comparatives.medicare_rate)}
                    </p>
                  </div>
                )}

                {pricingContext.market_comparatives.medicaid_rate && (
                  <div className="text-center p-2 bg-green-50 rounded">
                    <Heart className="h-4 w-4 mx-auto text-green-600 mb-1" />
                    <p className="text-xs text-gray-500">{t('serviceReferrals.pricing.medicaid', 'Medicaid')}</p>
                    <p className="font-semibold text-sm">
                      {formatCurrency(pricingContext.market_comparatives.medicaid_rate)}
                    </p>
                  </div>
                )}

                {pricingContext.market_comparatives.commercial_rate && (
                  <div className="text-center p-2 bg-purple-50 rounded">
                    <Briefcase className="h-4 w-4 mx-auto text-purple-600 mb-1" />
                    <p className="text-xs text-gray-500">{t('serviceReferrals.pricing.commercial', 'Commercial')}</p>
                    <p className="font-semibold text-sm">
                      {formatCurrency(pricingContext.market_comparatives.commercial_rate)}
                    </p>
                  </div>
                )}
              </div>

              {pricingContext.market_comparatives.market_average && (
                <div className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                  <span className="text-gray-600">{t('serviceReferrals.pricing.marketAverage', 'Market Average')}</span>
                  <span className="font-medium">
                    {formatCurrency(pricingContext.market_comparatives.market_average)}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Billing Information */}
        {pricingContext?.service_type && (
          <>
            <Separator className="my-4" />
            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
              {pricingContext.service_type.cpt_code && (
                <Badge variant="outline">CPT: {pricingContext.service_type.cpt_code}</Badge>
              )}
              {pricingContext.service_type.hcpcs_code && (
                <Badge variant="outline">HCPCS: {pricingContext.service_type.hcpcs_code}</Badge>
              )}
              <Badge variant="outline">
                Unit: {pricingContext.service_type.billing_unit?.replace('_', ' ')}
              </Badge>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default PricingContextPanel;
